import * as React from "react";
import { useStaticQuery, graphql } from 'gatsby';
import { Button, Box, Container, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";


import Slider from 'components/Slider';
import SliderArrows from 'components/SliderArrows';
import ZBox from 'components/ZBox';
import ArticleCard from 'components/Cards/ArticleCard';
import useLiveData from 'hooks/useLiveData';
import theme, { getColor } from 'utils/theme';

import useSliderPause from 'hooks/useSliderPause';


/**
 * @param string title The title of the section that is displayed above the slider.
 * @param string link The url to be used as the href for the CTA button.
 * @param string linkText The text of the CTA button.
 * @param mixed [blacklist] Can be either a String or an Array of ID's to be excluded from the useLiveData response.
 * @param object [data] Optional data object to be used in place of the data returned by the useLiveData hook.
 * 						The object should contain array of objects at the node level. 
 * 						This will essentially turn this component into a controlled component. 
 */ 

const InsightsSlider = props => {

	const { isPaused } = useSliderPause();
	
	const { title, link, linkText, blacklist, data: controlledData } = props;

	const disallow = typeof blacklist === 'string' ? [blacklist] : blacklist || [];

	const arrowRef = React.useRef([null, null]);
	const [initialize, setInitialize] = React.useState(false);

	React.useEffect(() => {
		setInitialize(true);
	},[]);
	
	const data = useStaticQuery(graphql`
		query insightsSlider {
			allBlogPost(
				filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}
				sort: [{showDate: DESC}]
				limit: 6
			) {
		      edges {
				node {
					id
					uri
					title
					intro
					photo
					Topic {
						id
						object {
							name
						}
					}
				}
			  }
		  }
		}
	`);

	const { edges } = data.allBlogPost;
	const nodes = edges.map(({ node }) => node);

	const { statusText: providerStatusText, status: providerStatus, response: providerResponse, visibilityChange } = useLiveData({
		type: 'BlogPost',
		apiParams: {
			approved: true,
			deleted: false,
			archived: false,
			_join: 'Topic',
			_limit: 5,
			_exclude: 'Author,body',
			_include: 'photo,title,intro,uri',
			_order: 'DESC',
			_sort: 'showDate',
			'!id': disallow,
		},
		staticData: controlledData ?? data,
	});

	let posts = nodes;
	if (((providerResponse && providerResponse.data.length > 0) || posts.length > 0) && providerStatusText === 'LOADED') {
		posts = providerResponse.data;
	}
	if (providerResponse && visibilityChange) posts = providerResponse.data;
	if (controlledData) posts = controlledData;
	if (posts.length === 0 && providerStatus >= 2) return null;

	if (disallow.length) posts = posts.filter(post => !disallow.includes(post.id));

	const sectionStyle = {
		backgroundColor: getColor('primary', 500),
		padding: '81px 0 73px',
		overflow: 'hidden',
		[theme.breakpoints.down('sm')]: {
			padding: '50px 0 73px',
		}
	};

	const sliderParams = {
		slidesPerView: '1.1',
		spaceBetween: 20,
		pagination: false,
		navigation: {
			prevEl: arrowRef.current[0],
			nextEl: arrowRef.current[1],
		},
		injectStyles: [
			`
			.swiper {
				overflow: visible;
			}
			`
		],
		breakpoints: {
			[theme.breakpoints.values.laptop]: {
				slidesPerView: 2,
				spaceBetween: 20,
			}
		},
	};


	return (
		<Box sx={sectionStyle} component={"section"}>
			<Container>
				<ZBox className={'slider__title_holder'} sx={{ marginBottom: "38px" }}>
					<Typography variant={"h1"} component={"h2"}>{title}</Typography>
					<SliderArrows refFunction={arrowRef} />
				</ZBox>
				<Slider params={sliderParams} initialize={initialize} isPaused={isPaused}>
					{posts.map((post, index) => {
						let intro = '';
						if (post?.intro) {
							let words = post.intro.split(' ');
							words.length = 20;
							intro = `${words.join(' ')}...`;
						};
					return (
						<swiper-slide style={{height: 'auto'}} key={`card-${index}-${post.id}`}>
							<ArticleCard 
								link={post?.uri}
								title={post?.title}
								body={intro}
								image={post?.photo ?? '/userfiles/17ZEH052_About_office1.jpg'}
								topic={post?.Topic}
							/>
						</swiper-slide>
					)})}
				</Slider>
				<Grid sx={{
						marginTop: '1.5rem', [theme.breakpoints.down('md')]: {
							marginTop: "1.375rem", marginBottom: "2rem", display: 'none'
						}
					}} size={1} offset={10} display="flex" justifyContent="flex-end" alignItems="center">
					</Grid>
				{link && <Box sx={{ textAlign: "center", marginTop: "0px", [theme.breakpoints.down('md')]: {
							marginTop: "55px",
						} }}><Button variant={"outlined"} color={"white"} href={link}>{linkText}</Button></Box>}
			</Container>
		</Box>
	)
}

export default InsightsSlider;